import React, { useEffect, useState } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  TreeSelect, 
  Row, 
  Col, 
  message,
  Checkbox,
  Switch,
  Divider,
  Alert,
  Spin
} from 'antd';
import { 
  UserOutlined, 
  IdcardOutlined, 
  PhoneOutlined, 
  MailOutlined,
  DollarOutlined,
  PercentageOutlined,
  BookOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { TeacherList, teacherAPI, TeacherCreate, TeacherDetailResponse } from '../../services/teacherAPI';
import { UserCompany, Company } from '../../services/companyAPI';
import { specialtyAPI, SpecialtySelect } from '../../services/specialtyAPI';

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

interface TeacherFormProps {
  visible: boolean;
  teacher: TeacherList | null;
  onCancel: () => void;
  onSuccess: () => void;
  selectedCompany: SelectedCompany | null;
}

const TeacherForm: React.FC<TeacherFormProps> = ({ 
  visible, 
  teacher, 
  onCancel, 
  onSuccess, 
  selectedCompany 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [specialtyOptions, setSpecialtyOptions] = useState<any[]>([]);
  const [finCodeChecking, setFinCodeChecking] = useState(false);
  const [idNumberChecking, setIdNumberChecking] = useState(false);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [checkingFin, setCheckingFin] = useState(false);
  const [checkingId, setCheckingId] = useState(false);

  useEffect(() => {
    if (visible && selectedCompany) {
      loadSpecialtyOptions();
      
      if (teacher) {
        // Redaktə rejimi - mövcud müəllim məlumatlarını yüklə
        loadTeacherDetails();
      } else {
        // Yeni müəllim əlavə etmə rejimi
        form.resetFields();
        form.setFieldsValue({ 
          company: selectedCompany.id,
          is_active: true,
          work_percentage: 100,
          exam_panel_access: false,
          exam_username: undefined,
          exam_password: undefined
        });
      }
    }
  }, [visible, teacher, selectedCompany]);

  const loadTeacherDetails = async () => {
    if (!teacher) return;
    
    try {
      const response = await teacherAPI.getTeacher(teacher.id);
      if (response.success && response.data) {
        const teacherData = response.data;
        form.setFieldsValue({
          first_name: teacherData.first_name,
          last_name: teacherData.last_name,
          father_name: teacherData.father_name,
          fin_code: teacherData.fin_code,
          id_number: teacherData.id_number,
          work_percentage: teacherData.work_percentage,
          salary: teacherData.salary,
          phone: teacherData.phone,
          email: teacherData.email,
          company: teacherData.company,
          is_active: teacherData.is_active,
          specialties: teacherData.specialties_detail.map(tc => tc.specialty),
          // İmtahan paneli məlumatları
          exam_panel_access: teacherData.exam_panel_access || false,
          exam_username: teacherData.exam_username || undefined,
          // Şifrə boş saxlanır (təhlükəsizlik üçün), istifadəçi yenisini daxil edə bilər
          exam_password: undefined
        });
      }
    } catch (error) {
      console.error('Müəllim məlumatları yüklənmədi:', error);
      message.error('Müəllim məlumatları yüklənmədi');
    }
  };

  const loadSpecialtyOptions = async () => {
    if (!selectedCompany) return;
    
    setLoadingSpecialties(true);
    try {
      const response = await specialtyAPI.getSpecialtySelectOptions(selectedCompany.id);
      if (response.success && response.data) {
        // TreeSelect üçün hierarchical struktur yaradırıq
        const treeData = buildTreeData(response.data);
        setSpecialtyOptions(treeData);
      }
    } catch (error) {
      console.error('İxtisaslar yüklənmədi:', error);
      message.error('İxtisaslar yüklənmədi');
    } finally {
      setLoadingSpecialties(false);
    }
  };

  // FIN kodu yoxlama funksiyası
  const checkFinCodeExists = async (finCode: string): Promise<void> => {
    if (!finCode || finCode.length !== 7 || !selectedCompany) return;
    
    setCheckingFin(true);
    try {
      const response = await teacherAPI.checkFinCodeExists(finCode, selectedCompany.id, teacher?.id);
      if (response.exists) {
        const errorMessage = `Bu FIN kodu ilə müəllim artıq mövcuddur: ${response.teacherName}`;
        setValidationErrors(prev => ({
          ...prev,
          fin_code: errorMessage
        }));
        throw new Error(errorMessage);
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.fin_code;
          return newErrors;
        });
      }
    } catch (error: any) {
      if (error.message.includes('mövcuddur')) {
        throw error;
      }
      console.error('FIN kodu yoxlanılarkən xəta:', error);
    } finally {
      setCheckingFin(false);
    }
  };

  // ID nömrəsi yoxlama funksiyası
  const checkIdNumberExists = async (idNumber: string): Promise<void> => {
    if (!idNumber || idNumber.length !== 9 || !selectedCompany) return;
    
    setCheckingId(true);
    try {
      const response = await teacherAPI.checkIdNumberExists(idNumber, selectedCompany.id, teacher?.id);
      if (response.exists) {
        const errorMessage = `Bu seriya nömrəsi ilə müəllim artıq mövcuddur: ${response.teacherName}`;
        setValidationErrors(prev => ({
          ...prev,
          id_number: errorMessage
        }));
        throw new Error(errorMessage);
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.id_number;
          return newErrors;
        });
      }
    } catch (error: any) {
      if (error.message.includes('mövcuddur')) {
        throw error;
      }
      console.error('ID nömrəsi yoxlanılarkən xəta:', error);
    } finally {
      setCheckingId(false);
    }
  };

  const buildTreeData = (specialties: SpecialtySelect[]) => {
    const specialtyMap = new Map();
    const rootSpecialties: any[] = [];

    // Əvvəlcə bütün ixtisasları map-ə əlavə et
    specialties.forEach(specialty => {
      specialtyMap.set(specialty.value, {
        title: specialty.label,
        value: specialty.value,
        key: specialty.value,
        children: [],
        level: specialty.level,
        parent: specialty.parent
      });
    });

    // Sonra parent-child əlaqələrini qur
    specialties.forEach(specialty => {
      const specialtyNode = specialtyMap.get(specialty.value);
      if (specialty.parent) {
        const parentNode = specialtyMap.get(specialty.parent);
        if (parentNode) {
          parentNode.children.push(specialtyNode);
        }
      } else {
        rootSpecialties.push(specialtyNode);
      }
    });

    return rootSpecialties;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      setValidationErrors({});

      const teacherData: TeacherCreate = {
        first_name: values.first_name,
        last_name: values.last_name,
        father_name: values.father_name,
        fin_code: values.fin_code,
        id_number: values.id_number,
        work_percentage: values.work_percentage,
        salary: values.salary,
        phone: values.phone || '',
        email: values.email || '',
        company: values.company,
        is_active: values.is_active,
        specialties: values.specialties || [],
        // İmtahan paneli hesabı
        exam_panel_access: values.exam_panel_access || false,
        exam_username: values.exam_username || undefined,
        exam_password: values.exam_password || undefined
      };

      let response: TeacherDetailResponse;
      if (teacher) {
        response = await teacherAPI.updateTeacher(teacher.id, teacherData);
      } else {
        response = await teacherAPI.createTeacher(teacherData);
      }

      if (response.success) {
        message.success(response.message || (teacher ? 'Müəllim yeniləndi' : 'Müəllim əlavə edildi'));
        onSuccess();
      } else {
        // Server-dən gələn xətaları işlə
        if (response.errors) {
          const newErrors: {[key: string]: string} = {};
          
          Object.keys(response.errors).forEach(key => {
            const errorValue = response.errors![key];
            const errorMessage = Array.isArray(errorValue) 
              ? errorValue[0] 
              : errorValue;
            
            newErrors[key] = errorMessage;
            
            // Xüsusi xəta mesajlarını göstər
            if (key === 'fin_code') {
              message.error(errorMessage);
            } else if (key === 'id_number') {
              message.error(errorMessage);
            } else if (key === 'non_field_errors') {
              message.error(errorMessage);
            }
          });
          
          setValidationErrors(newErrors);
          
          // Form sahələrində xətaları göstər
          Object.keys(newErrors).forEach(key => {
            form.setFields([{
              name: key,
              errors: [newErrors[key]]
            }]);
          });
          
        } else {
          message.error(response.message || 'Əməliyyat uğursuz oldu');
        }
      }
    } catch (error: any) {
      console.error('Form xətası:', error);
      if (error.errorFields) {
        message.error('Form məlumatlarını yoxlayın');
      } else {
        message.error('Xəta baş verdi');
      }
    } finally {
      setLoading(false);
    }
  };

  // FIN kod yoxlama funksiyası
  const checkFinCode = async (finCode: string) => {
    if (!finCode || finCode.length !== 7) return;
    if (teacher && teacher.fin_code === finCode) return;
    
    setFinCodeChecking(true);
    try {
      const response = await teacherAPI.getTeachers({ 
        search: finCode,
        company: selectedCompany?.id 
      });
      
      const existingTeacher = response.data?.find((t: any) => 
        t.fin_code === finCode && t.id !== teacher?.id
      );
      
      if (existingTeacher) {
        form.setFields([{
          name: 'fin_code',
          errors: [`Bu FIN kod artıq mövcuddur (Müəllim: ${existingTeacher.full_name})`]
        }]);
      }
    } catch (error) {
      console.error('FIN kod yoxlama xətası:', error);
    } finally {
      setFinCodeChecking(false);
    }
  };

  // ID nömrəsi yoxlama funksiyası
  const checkIdNumber = async (idNumber: string) => {
    if (!idNumber || idNumber.length < 5) return;
    if (teacher && teacher.id_number === idNumber) return;
    
    setIdNumberChecking(true);
    try {
      const response = await teacherAPI.getTeachers({ 
        search: idNumber,
        company: selectedCompany?.id 
      });
      
      const existingTeacher = response.data?.find((t: any) => 
        t.id_number === idNumber && t.id !== teacher?.id
      );
      
      if (existingTeacher) {
        form.setFields([{
          name: 'id_number',
          errors: [`Bu vəsiqə nömrəsi artıq mövcuddur (Müəllim: ${existingTeacher.full_name})`]
        }]);
      }
    } catch (error) {
      console.error('ID nömrəsi yoxlama xətası:', error);
    } finally {
      setIdNumberChecking(false);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center">
          <UserOutlined className="mr-2 text-blue-500" />
          {teacher ? 'Müəllimi Redaktə Et' : 'Yeni Müəllim Əlavə Et'}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      okText={teacher ? 'Yenilə' : 'Əlavə Et'}
      cancelText="Ləğv Et"
      destroyOnHidden
      okButtonProps={{
        disabled: Object.keys(validationErrors).length > 0 || checkingFin || checkingId
      }}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        {Object.keys(validationErrors).length > 0 && (
          <Alert
            title="Validasiya xətaları"
            description={
              <ul className="mb-0">
                {Object.entries(validationErrors).map(([key, error]) => (
                  <li key={key}>{error}</li>
                ))}
              </ul>
            }
            type="error"
            showIcon
            className="mb-4"
            closable
            onClose={() => setValidationErrors({})}
          />
        )}

        <Alert
          title="Vacib sahələr"
          description="Ad, soyad, ata adı, FIN kodu, seriya nömrəsi, işləmə faizi və maaş sahələri mütləq doldurulmalıdır."
          type="info"
          showIcon
          className="mb-6"
        />

        {/* Şəxsi məlumatlar */}
        <Divider>
          <IdcardOutlined className="mr-2" />
          Şəxsi məlumatlar
        </Divider>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="first_name"
              label="Ad"
              rules={[
                { required: true, message: 'Ad tələb olunur!' },
                { min: 2, message: 'Ad ən azı 2 simvol olmalıdır!' }
              ]}
            >
              <Input 
                placeholder="Müəllimin adı"
                prefix={<UserOutlined className="text-gray-400" />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="last_name"
              label="Soyad"
              rules={[
                { required: true, message: 'Soyad tələb olunur!' },
                { min: 2, message: 'Soyad ən azı 2 simvol olmalıdır!' }
              ]}
            >
              <Input 
                placeholder="Müəllimin soyadı"
                prefix={<UserOutlined className="text-gray-400" />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="father_name"
              label="Ata adı"
              rules={[
                { required: true, message: 'Ata adı tələb olunur!' },
                { min: 2, message: 'Ata adı ən azı 2 simvol olmalıdır!' }
              ]}
            >
              <Input 
                placeholder="Müəllimin ata adı"
                prefix={<UserOutlined className="text-gray-400" />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="fin_code"
              label="FIN kodu"
              hasFeedback
              validateStatus={finCodeChecking ? 'validating' : undefined}
              rules={[
                { required: true, message: 'FIN kodu tələb olunur!' },
                { len: 7, message: 'FIN kodu 7 simvol olmalıdır!' },
                { pattern: /^[A-Z0-9]{7}$/, message: 'FIN kodu yalnız böyük hərf və rəqəmlərdən ibarət olmalıdır!' }
              ]}
            >
              <Input 
                placeholder="A1B2C3D"
                maxLength={7}
                style={{ textTransform: 'uppercase' }}
                prefix={<IdcardOutlined className="text-gray-400" />}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  form.setFieldsValue({ fin_code: value });
                  form.setFields([{ name: 'fin_code', errors: [] }]);
                }}
                onBlur={(e) => {
                  const value = e.target.value.toUpperCase();
                  if (value.length === 7) {
                    checkFinCode(value);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="id_number"
              label="Seriya nömrəsi"
              hasFeedback
              validateStatus={idNumberChecking ? 'validating' : undefined}
              rules={[
                { required: true, message: 'Seriya nömrəsi tələb olunur!' },
                { min: 5, message: 'Ən azı 5 simvol olmalıdır' },
                { max: 20, message: 'Maksimum 20 simvol ola bilər' }
              ]}
            >
              <Input 
                placeholder="AA1234567, AZE55555, və s."
                maxLength={20}
                style={{ textTransform: 'uppercase' }}
                prefix={<IdcardOutlined className="text-gray-400" />}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  form.setFieldsValue({ id_number: value });
                  form.setFields([{ name: 'id_number', errors: [] }]);
                }}
                onBlur={(e) => {
                  const value = e.target.value.toUpperCase();
                  if (value.length >= 5) {
                    checkIdNumber(value);
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* İş məlumatları */}
        <Divider>
          <DollarOutlined className="mr-2" />
          İş məlumatları
        </Divider>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="work_percentage"
              label="İşləmə faizi (%)"
              rules={[
                { required: true, message: 'İşləmə faizi tələb olunur!' },
                { 
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.reject(new Error('İşləmə faizi tələb olunur!'));
                    }
                    if (value < 1 || value > 100) {
                      return Promise.reject(new Error('İşləmə faizi 1-100 arası olmalıdır!'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <InputNumber
                min={1}
                max={100}
                placeholder="100"
                style={{ width: '100%' }}
                prefix={<PercentageOutlined className="text-gray-400" />}
                formatter={value => `${value}%`}
                parser={(value) => {
                  const parsed = value!.replace('%', '');
                  return parsed as any;
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="salary"
              label="Maaş (AZN)"
              rules={[
                { required: true, message: 'Maaş tələb olunur!' },
                { 
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.reject(new Error('Maaş tələb olunur!'));
                    }
                    if (value < 0) {
                      return Promise.reject(new Error('Maaş müsbət rəqəm olmalıdır!'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <InputNumber
                min={0}
                step={0.01}
                placeholder="1000.00"
                style={{ width: '100%' }}
                prefix={<DollarOutlined className="text-gray-400" />}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => {
                  const parsed = value!.replace(/\$\s?|(,*)/g, '');
                  return parsed as any;
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Əlaqə məlumatları */}
        <Divider>
          <PhoneOutlined className="mr-2" />
          Əlaqə məlumatları
        </Divider>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="phone"
              label="Telefon"
              rules={[
                { pattern: /^\+994[0-9]{9}$/, message: 'Telefon +994501234567 formatında olmalıdır!' }
              ]}
            >
              <Input 
                placeholder="+994501234567"
                prefix={<PhoneOutlined className="text-gray-400" />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { type: 'email', message: 'Düzgün email ünvanı daxil edin!' }
              ]}
            >
              <Input 
                placeholder="muellim@sirket.com"
                prefix={<MailOutlined className="text-gray-400" />}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* İxtisaslar */}
        <Divider>
          <BookOutlined className="mr-2" />
          Tədris etdiyi ixtisaslar
        </Divider>

        <Form.Item
          name="specialties"
          label="İxtisaslar"
          help="Müəllimin tədris edəcəyi ixtisasları seçin"
        >
          <TreeSelect
            multiple
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_ALL}
            placeholder="İxtisasları seçin..."
            treeData={specialtyOptions}
            loading={loadingSpecialties}
            style={{ width: '100%' }}
            styles={{ popup: { root: { maxHeight: 400, overflow: 'auto' } } }}
            treeDefaultExpandAll
            allowClear
          />
        </Form.Item>

        {/* Status */}
        <Divider>Status</Divider>

        <Form.Item
          name="is_active"
          label="Aktiv status"
          valuePropName="checked"
        >
          <Switch 
            checkedChildren="Aktiv" 
            unCheckedChildren="Deaktiv"
          />
        </Form.Item>

        {/* İmtahan Paneli Hesabı */}
        <Row gutter={16} style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
          <Col span={24}>
            <h4 style={{ marginBottom: 16, color: '#1890ff' }}>İmtahan Paneli Hesabı</h4>
            <p style={{ marginBottom: 16, color: '#666' }}>
              Müəllimə imtahan panelinə giriş üçün istifadəçi adı və şifrə təyin edin.
            </p>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="exam_panel_access"
              valuePropName="checked"
            >
              <Checkbox>İmtahan paneli hesabı yarat</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => 
            prevValues.exam_panel_access !== currentValues.exam_panel_access
          }
        >
          {({ getFieldValue }) => {
            const createAccount = getFieldValue('exam_panel_access');
            return createAccount ? (
              <>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="exam_username"
                      label="İmtahan paneli istifadəçi adı"
                      rules={[
                        { required: true, message: 'İstifadəçi adı tələb olunur!' },
                        { min: 3, message: 'İstifadəçi adı ən azı 3 simvol olmalıdır!' }
                      ]}
                      extra="Bu istifadəçi adı ilə müəllim imtahan panelinə daxil ola biləcək"
                    >
                      <Input 
                        placeholder="İstifadəçi adı daxil edin (məsələn: muellim123)"
                        autoComplete="off"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="exam_password"
                      label="İmtahan paneli şifrəsi"
                      rules={[
                        { 
                          required: !teacher, 
                          message: 'Şifrə tələb olunur!' 
                        },
                        { 
                          min: 6, 
                          message: 'Şifrə ən azı 6 simvol olmalıdır!',
                          validateTrigger: 'onBlur'
                        }
                      ]}
                      extra={teacher ? "Boş saxlasanız köhnə şifrə qalacaq" : "Bu şifrə ilə müəllim imtahan panelinə daxil ola biləcək"}
                    >
                      <Input.Password 
                        placeholder={teacher ? "Yeni şifrə (boş saxlasanız köhnə şifrə qalacaq)" : "Şifrə daxil edin"}
                        autoComplete="new-password"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : null;
          }}
        </Form.Item>

        {/* Gizli sahələr */}
        <Form.Item name="company" hidden>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TeacherForm;